import { CLIENT_MESSAGE_TYPE } from '@orn/global/assistant/wireProtocol.js';
import * as React from 'react';

import {
  isExpandAlreadyPending,
  takeNextTurn,
  toUserMessagePart,
  type PendingTurnFrame,
  type PendingUserMessage,
} from '../../lib/assistant/pendingTurnQueue';
import { generateRowId } from '../../lib/assistant/rowIds';
import { requeueSupersededTurn } from '../../lib/assistant/supersededRequeue';
import { dropMessagesByIds } from '../../lib/assistantSnapshot';

import type { IConversationMessage, IToolExecution } from '../useAssistantWebSocket';
import type { IObservationDocument } from '@orn/global/assistant/snapshotMessage.js';

/** A ref whose `current` this hook writes. Spelled locally to stay off React's shifting ref type names. */
type MutableRef<T> = { current: T };

/** What {@link useAssistantTurnLifecycle} needs from the socket hook to run a turn. */
export interface IAssistantTurnLifecycleDeps {
  /** The live socket. Every outbound frame goes out on it, gated on `readyState === OPEN`. */
  wsRef: MutableRef<WebSocket | null>;
  /** True once the server's `assistant_ready` has landed. Gates every outbound frame. */
  readyRef: MutableRef<boolean>;
  /** True while this tab is superseded by a newer one — holds the queue rather than auto-dialing. */
  supersededRef: MutableRef<boolean>;
  /** Always-current mirror of the rendered transcript, read when a supersession re-queues. */
  messagesRef: MutableRef<IConversationMessage[]>;
  connect: () => void;
  setMessages: React.Dispatch<React.SetStateAction<IConversationMessage[]>>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  setIsProcessing: React.Dispatch<React.SetStateAction<boolean>>;
  setIsAbortable: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * The turn controls the socket hook drives — from its inbound message switch,
 * its close handler, and the two actions the pane exposes.
 */
export interface IAssistantTurnLifecycle {
  /**
   * True while the server is processing a turn for this connection. Read
   * synchronously by every send path to decide ship-now vs queue, and set
   * directly by the `status: 'thinking'` frame so a SERVER-initiated turn (the
   * proactive bootstrap) also closes the gate.
   */
  turnInFlightRef: MutableRef<boolean>;
  /**
   * Id of the assistant bubble currently being grown by
   * `assistant_message_delta` frames, or `null` when no round is mid-stream. A
   * private id cache — NOT a second source of truth (the bubble's own
   * `isStreaming` flag is that) — so every `setMessages` updater stays pure.
   */
  streamingRowIdRef: MutableRef<string | null>;
  /** Per-round tool buffer, kept solely to assemble the debug-console log line. */
  toolExecutionsRef: MutableRef<IToolExecution[]>;
  startTurn: () => void;
  /**
   * Close out the turn and retract anything it left on screen.
   *
   * @returns the alert an expand turn was opened on, or `null`. Returned rather
   * than left on a ref for the caller to read first, because the close handler
   * needs it *after* the turn is torn down — reading it beforehand was an
   * ordering the type could not enforce.
   */
  endTurn: () => string | null;
  flushPendingQueue: () => void;
  sendUserMessage: (content: string, attachments?: IObservationDocument[]) => void;
  expandAlert: (alertId: string) => void;
  /** Put a superseded turn back at the front of the queue and drop its bubbles to queued. */
  requeueSuperseded: (supersededExpandId: string | null) => void;
  /** Record a row this turn put on screen, to be taken back unless the turn lands. */
  trackTurnRow: (rowId: string) => void;
  /** Release this turn's rows — the transcript owns them now, so `endTurn` must not retract them. */
  commitTurnRows: () => void;
  /**
   * The in-flight turn's `turnKey`, minted on first use and held until the turn
   * ends. Stamped on every row a live turn puts on screen so the transcript can
   * group rows by turn the same way rehydrated rows group by their persisted
   * `turn_id` — equality within one transcript is all consumers read, so the
   * two id spaces never need to agree. Lazy because a server-initiated turn
   * never passes through `startTurn`; its first row mints the key.
   */
  currentTurnKey: () => string;
}

/**
 * Everything that makes a turn either reach the transcript whole or not at all:
 * the outbound turn queue, the in-flight turn's flags, and the bookkeeping that
 * takes a turn's rows back when it does not land.
 *
 * The two are one concern because the queue is what decides when a turn starts
 * and the row list is what decides what survives it — split apart, the rule
 * "the live transcript says what a reload would" has no single home. The socket
 * hook owns the connection and the inbound message switch, and calls in here.
 *
 * @param deps - see {@link IAssistantTurnLifecycleDeps}; `deps.connect` must be
 * referentially stable, and the refs are read synchronously from inside socket
 * event callbacks where a committed render is not guaranteed.
 * @returns the controls in {@link IAssistantTurnLifecycle}, each stable across
 * renders, plus the three refs the message switch reads and writes directly.
 */
export function useAssistantTurnLifecycle(deps: IAssistantTurnLifecycleDeps): IAssistantTurnLifecycle {
  const {
    wsRef,
    readyRef,
    supersededRef,
    messagesRef,
    connect,
    setMessages,
    setError,
    setIsProcessing,
    setIsAbortable,
  } = deps;

  // FIFO queue of outbound turn frames that arrived while the WS was
  // disconnected, before `assistant_ready` landed, or while a turn was already
  // in flight (mid-turn typing, or an alert hand-off drained while a turn
  // runs — the alert surfaces themselves refuse direct clicks mid-turn). One
  // queue rather than two so the user's actions replay in the order they took
  // them — a message typed before an alert click must reach the server first.
  const pendingFramesRef = React.useRef<PendingTurnFrame[]>([]);
  const turnInFlightRef = React.useRef(false);
  const streamingRowIdRef = React.useRef<string | null>(null);
  const toolExecutionsRef = React.useRef<IToolExecution[]>([]);

  /**
   * Every row this turn has put on screen — the card it opened with, its
   * tool-activity lines, and each reply bubble — held until the turn is known to
   * be in history.
   *
   * The server elides a turn wholesale when it errors, and drops one that banked
   * no round before a stop or produced no text at all, reporting
   * `turn_complete.discarded`. So "some of this turn survived" is never an
   * outcome: either the whole turn rehydrates or none of it does. Anything still
   * listed here at `endTurn` belongs to a turn that did not make it.
   */
  const turnRowIdsRef = React.useRef<string[]>([]);

  /**
   * The alert the in-flight turn is expanding, or `null`. Read by
   * {@link isExpandAlreadyPending} to recognize a repeat click in the window
   * before the card lands.
   */
  const inFlightExpandIdRef = React.useRef<string | null>(null);

  /** The in-flight turn's minted key, or `null` between turns. */
  const turnKeyRef = React.useRef<string | null>(null);

  // Lazy so a server-initiated turn — which never passes through `startTurn` —
  // mints on its first row. Every path that ends a turn goes through `endTurn`;
  // the key ref rides that, exactly like `streamingRowIdRef`.
  const currentTurnKey = React.useCallback((): string => {
    turnKeyRef.current ??= `turn-${generateRowId()}`;
    return turnKeyRef.current;
  }, []);

  const startTurn = React.useCallback((): void => {
    toolExecutionsRef.current = [];
    streamingRowIdRef.current = null;
    turnRowIdsRef.current = [];
    // Minted eagerly so the turn's own user bubbles can carry it — the walk
    // that groups an alert block's rows needs the click and the reply keyed
    // to the same turn.
    turnKeyRef.current = `turn-${generateRowId()}`;
    // A new turn clears the last one's error. Every send path already did this
    // before calling in, but the queue drain does not go through one — and that
    // is the path a rejected `alert_expand` takes when a message is queued
    // behind it, which would otherwise pin the banner over a turn that went on
    // to succeed, with nothing left to clear it but a manual send.
    setError(null);
    setIsProcessing(true);
    turnInFlightRef.current = true;
    // Eligible to become abortable, but not yet — the agent run only goes live
    // (and `stop_turn` only does anything) once the server's `status:'thinking'`
    // lands. Until then the button stays a send button so the working-but-not-
    // yet-abortable window has no dead stop.
    setIsAbortable(false);
  }, [setError, setIsProcessing, setIsAbortable]);

  const endTurn = React.useCallback((): string | null => {
    const supersededExpandId = inFlightExpandIdRef.current;
    turnInFlightRef.current = false;
    streamingRowIdRef.current = null;
    inFlightExpandIdRef.current = null;
    turnKeyRef.current = null;
    // Rows still listed here belong to a turn that never reached the transcript
    // — an error, an abort, or a `discarded` completion. Taking them back is
    // what keeps the live view equal to the one a reload rebuilds: a
    // half-streamed bubble would otherwise linger looking like a complete
    // (truncated) reply, and a finalized one from an earlier round of the same
    // turn would outlive the turn it belonged to.
    const orphanedRowIds = turnRowIdsRef.current;
    if (orphanedRowIds.length > 0) {
      turnRowIdsRef.current = [];
      setMessages((prev) => dropMessagesByIds(prev, orphanedRowIds));
    }
    setIsProcessing(false);
    setIsAbortable(false);
    toolExecutionsRef.current = [];
    return supersededExpandId;
  }, [setMessages, setIsProcessing, setIsAbortable]);

  /**
   * Drain the head of the pending queue as a single turn (see
   * {@link takeNextTurn} for what "head" means — a run of user messages batches
   * into one `user_message` frame; an alert expand goes alone). Anything left
   * over drains on the next `turn_complete`.
   *
   * For a user batch, flips the matching bubbles from queued
   * (`isInFlight: false`) to in-flight (`isInFlight: true`) so the view moves
   * them above the thinking box. No-op if the WS is not OPEN + ready, the queue
   * is empty, or a turn is already in flight (the current turn must finish
   * before we drain — that's what gives us the batching window).
   */
  const flushPendingQueue = React.useCallback((): void => {
    if (pendingFramesRef.current.length === 0) {
      return;
    }
    const socket = wsRef.current;
    if (socket?.readyState !== WebSocket.OPEN || !readyRef.current) {
      return;
    }
    if (turnInFlightRef.current) {
      return;
    }

    const { turn, rest } = takeNextTurn(pendingFramesRef.current);
    pendingFramesRef.current = rest;

    const head = turn[0];
    if (head.kind === 'alert-expand') {
      startTurn();
      inFlightExpandIdRef.current = head.alertId;
      socket.send(JSON.stringify({ type: CLIENT_MESSAGE_TYPE.AlertExpand, alertId: head.alertId }));
      return;
    }

    const batch = turn as PendingUserMessage[];
    const batchedIds = new Set(batch.map((entry) => entry.clientMessageId));
    startTurn();
    // Stamped at drain, not at enqueue — a queued bubble belongs to whatever
    // turn eventually carries it, which is only decided here.
    const turnKey = currentTurnKey();
    setMessages((prev) =>
      prev.map((message) =>
        message.clientMessageId !== undefined && batchedIds.has(message.clientMessageId)
          ? { ...message, isInFlight: true, turnKey }
          : message
      )
    );
    socket.send(JSON.stringify({ type: CLIENT_MESSAGE_TYPE.UserMessage, messages: batch.map(toUserMessagePart) }));
  }, [wsRef, readyRef, setMessages, startTurn, currentTurnKey]);

  const sendUserMessage = React.useCallback(
    (content: string, attachments?: IObservationDocument[]): void => {
      // Derive the wire ids from the attachments array; the two are
      // structurally redundant (`ids = attachments.map(a => a.id)`), so
      // accepting both at the call site would be double-bookkeeping.
      const attachmentList = attachments ?? [];
      const ids = attachmentList.map((attachment) => attachment.id);
      if (!content.trim() && ids.length === 0) {
        return;
      }

      // The optimistic bubble's `clientMessageId` is also the wire id — server
      // persists it on the resulting user row and re-emits it on future
      // `transcript_snapshot`s, so the snapshot reconciler can dedupe by identity.
      const clientMessageId = crypto.randomUUID();

      // Three-way gate:
      //  - WS not OPEN OR not handshake-ready → queue (drained on `assistant_ready`).
      //  - A turn is already in flight → queue (drained on the current turn's
      //    terminal frame). This is the load-bearing branch that batches
      //    mid-turn typing into the next turn.
      //  - Otherwise → send immediately as a single-message turn.
      const socket = wsRef.current;
      const wsOpen = socket?.readyState === WebSocket.OPEN;
      const canSendNow = wsOpen && readyRef.current && !turnInFlightRef.current;

      // Started before the bubble is appended so the bubble can carry the new
      // turn's key; a queued bubble is stamped later, at drain, by
      // `flushPendingQueue`.
      if (canSendNow) {
        startTurn();
      }

      setMessages((prev) => [
        ...prev,
        {
          id: `msg-${generateRowId()}`,
          role: 'user',
          content,
          timestamp: new Date(),
          clientMessageId,
          isPending: true,
          // In-flight from this moment if we're sending now; queued otherwise.
          // The view splits these two states across the thinking box.
          isInFlight: canSendNow,
          ...(canSendNow ? { turnKey: currentTurnKey() } : {}),
          ...(attachmentList.length > 0 ? { attachments: attachmentList } : {}),
        },
      ]);

      setError(null);

      const wirePart: { content: string; clientMessageId: string; attachmentDocumentIds?: string[] } = {
        content,
        clientMessageId,
        ...(ids.length > 0 ? { attachmentDocumentIds: ids } : {}),
      };

      if (canSendNow && socket) {
        // One-entry `messages` array — the unified wire shape. The server treats
        // N = 1 identically to a batched N ≥ 2 frame.
        socket.send(JSON.stringify({ type: CLIENT_MESSAGE_TYPE.UserMessage, messages: [wirePart] }));
        return;
      }

      // Queue for the next drain. Trigger a connect when the WS isn't open; do
      // NOT connect when a turn is in flight (would kill the current turn). An
      // explicit user send is direct intent, so this `connect()` intentionally
      // bypasses the page-active deferral that gates automatic reconnects — a
      // queued message dials and drains even on a background or unfocused tab.
      pendingFramesRef.current = [...pendingFramesRef.current, { kind: 'user', ...wirePart }];
      // Dial a reconnect for an ordinary closed/paused socket — but NOT when
      // superseded. A superseded tab holds its queued messages until the user
      // explicitly clicks Reconnect; auto-dialing here would silently re-claim
      // the thread and evict the other tab.
      if (!wsOpen && !supersededRef.current) {
        connect();
      }
    },
    [wsRef, readyRef, supersededRef, connect, setMessages, setError, startTurn, currentTurnKey]
  );

  /**
   * Open an alert in the transcript. Same three-way gate as
   * {@link sendUserMessage}, and for a load-bearing reason beyond symmetry:
   * `startTurn` clears the streaming row id, so sending while a reply is
   * mid-stream would orphan that bubble — it would stop growing, never finalize,
   * and sit on screen as a truncated answer for the rest of the session.
   *
   * Nothing is appended locally. The card comes from the server's `alert_card`
   * frame, which is optimistic — it goes out before the turn runs, and so before
   * the row it corresponds to is persisted. What keeps a live transcript and a
   * reloaded one from disagreeing is the retraction: a turn that errors or
   * completes `discarded` takes the card back with the rest of its rows.
   */
  const expandAlert = React.useCallback(
    (alertId: string): void => {
      // Two fast clicks: the first opens the turn, the second finds it in flight
      // and would queue a duplicate that drains into a second persisted
      // exchange for the same alert. The strip cannot see this window — its
      // own guard reads the transcript, which has no card yet.
      if (isExpandAlreadyPending(alertId, inFlightExpandIdRef.current, pendingFramesRef.current)) {
        return;
      }

      const socket = wsRef.current;
      const wsOpen = socket?.readyState === WebSocket.OPEN;
      setError(null);

      if (wsOpen && socket && readyRef.current && !turnInFlightRef.current) {
        startTurn();
        inFlightExpandIdRef.current = alertId;
        socket.send(JSON.stringify({ type: CLIENT_MESSAGE_TYPE.AlertExpand, alertId }));
        return;
      }

      pendingFramesRef.current = [...pendingFramesRef.current, { kind: 'alert-expand', alertId }];
      if (!wsOpen && !supersededRef.current) {
        connect();
      }
    },
    [wsRef, readyRef, supersededRef, connect, setError, startTurn]
  );

  const requeueSuperseded = React.useCallback(
    (supersededExpandId: string | null): void => {
      // Reading the live transcript via `messagesRef` lets the ref mutation
      // happen here, OUTSIDE the updater, so `setMessages` stays a pure
      // projection (no side effect in a render function, no double-invoke
      // reasoning needed).
      pendingFramesRef.current = requeueSupersededTurn(
        messagesRef.current,
        pendingFramesRef.current,
        supersededExpandId
      );
      setMessages((prev) =>
        prev.map((message) =>
          message.isPending === true && message.isInFlight === true ? { ...message, isInFlight: false } : message
        )
      );
    },
    [messagesRef, setMessages]
  );

  const trackTurnRow = React.useCallback((rowId: string): void => {
    turnRowIdsRef.current.push(rowId);
  }, []);

  const commitTurnRows = React.useCallback((): void => {
    turnRowIdsRef.current = [];
  }, []);

  return {
    turnInFlightRef,
    streamingRowIdRef,
    toolExecutionsRef,
    startTurn,
    endTurn,
    flushPendingQueue,
    sendUserMessage,
    expandAlert,
    requeueSuperseded,
    trackTurnRow,
    commitTurnRows,
    currentTurnKey,
  };
}
